import type { Logger } from '@quromedical/logger';
import { set } from '@quromedical/utils';
import axios, { type AxiosError, type AxiosInstance } from 'axios';
import {
  HealthbridgeLoginClient,
  type OAuth2Credentials,
} from '../Authentication';

import type {
  CreateClaimErrorResponse,
  CreateClaimRequest,
  CreateClaimResponse,
  CreateClaimReversalErrorResponse,
  CreateClaimReversalRequest,
  CreateClaimReversalResponse,
  CreateClaimReversalSuccessResponse,
  CreateClaimSuccessResponse,
} from './';

type ClaimClientOptions = {
  baseUrl: string;
  credentials: OAuth2Credentials;
  logger: Logger;
};

export class ClaimClient {
  private apiVersion = '1.0';
  private basePath = `/claim-service/${this.apiVersion}/`;
  private logger: Logger;
  private client: AxiosInstance;

  constructor({ baseUrl, credentials, logger }: ClaimClientOptions) {
    this.client = ClaimClient.createAuthenticatedClient(baseUrl, credentials);
    this.logger = logger;
  }

  private static createAuthenticatedClient = (
    baseURL: string,
    credentials: OAuth2Credentials,
  ): AxiosInstance => {
    const client = axios.create({
      baseURL,
    });

    client.interceptors.request.use(async (request) => {
      const loginClient = new HealthbridgeLoginClient(baseURL, credentials);
      const authToken = await loginClient.getToken();

      set(request, 'headers.Authorization', `Bearer ${authToken}`);

      return request;
    });

    return client;
  };

  /**
   * Will submit a claim to the healthbridge API. Will respond with the error detail if the API call
   * is not successful
   */
  public createClaim = async (
    claim: CreateClaimRequest,
  ): Promise<CreateClaimResponse> => {
    try {
      const result = await this.client.post<CreateClaimSuccessResponse>(
        `${this.basePath}claim`,
        claim,
      );

      this.logger.info(`Successfully created claim`);
      return result.data;
    } catch (err) {
      const axiosError = err as AxiosError<CreateClaimErrorResponse>;

      if (!axiosError.response?.data) {
        const errors = axiosError.response?.data.Errors;
        return {
          Message: 'Healthbridge API did not send back a response',
          Errors: errors || [],
        };
      }

      return axiosError.response.data;
    }
  };

  /**
   * Will reverse a claim to the healthbridge API. Will respond with the error detail if the API call
   * is not successful
   */
  public reverseClaim = async (
    claimReversal: CreateClaimReversalRequest,
  ): Promise<CreateClaimReversalResponse> => {
    try {
      const result = await this.client.post<CreateClaimReversalSuccessResponse>(
        `${this.basePath}reversal`,
        claimReversal,
      );

      this.logger.info(`Successfully reversed claim`);
      return result.data;
    } catch (err) {
      const axiosError = err as AxiosError<CreateClaimReversalErrorResponse>;

      if (!axiosError.response?.data) {
        const errors = axiosError.response?.data.Errors;
        return {
          Message: 'Healthbridge API did not send back a response',
          Errors: errors || [],
        };
      }

      return axiosError.response.data;
    }
  };
}
